<template>
  <div>
    <!-- Sub-tab bar -->
    <div class="flex space-x-6 border-b mb-6">
      <button
        v-for="tab in tabs" :key="tab.key"
        @click="currentForm = tab.key"
        class="px-4 py-2 font-medium text-sm border-b-4 transition-all duration-200"
        :class="{
          'border-custom-yellow text-custom-blue': currentForm === tab.key,
          'border-transparent text-gray-500 hover:text-gray-700': currentForm !== tab.key
        }"
      >{{ tab.label }}</button>
    </div>

    <!-- MOH 706 -->
    <section v-if="currentForm === 'moh706'">
      <div class="flex items-center justify-between mb-3">
        <div>
          <h2 class="text-lg font-bold text-gray-800">MOH 706 — Laboratory Register</h2>
          <p class="text-sm text-gray-500">Published lab tests for the selected period</p>
        </div>
        <button
          v-if="moh706.data.length"
          @click="printSection('moh706-content')"
          class="px-4 py-2 border border-custom-blue text-custom-blue rounded hover:bg-blue-50 text-sm font-medium"
        >Print MOH 706</button>
      </div>
      <div class="flex items-center gap-4 mb-4">
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">From:</label>
          <input v-model="moh706.from" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">To:</label>
          <input v-model="moh706.to" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <button @click="fetchMoh706" :disabled="moh706.loading"
          class="px-5 py-2 bg-custom-yellow text-custom-blue font-semibold rounded hover:bg-yellow-400 text-sm">
          {{ moh706.loading ? 'Loading...' : 'Generate' }}
        </button>
      </div>
      <div id="moh706-content">
        <div class="print:block hidden mb-4">
          <div class="flex flex-col items-center mb-3">
            <img v-if="facility.logo_url" :src="facility.logo_url" alt="Logo" class="h-16 object-contain mb-1" />
            <p class="font-bold text-center">{{ facility.FacilityName }}</p>
            <p v-if="facilityLocation" class="text-xs text-center text-gray-600">{{ facilityLocation }}</p>
          </div>
          <h1 class="text-xl font-bold text-center">MOH 706 — Laboratory Register</h1>
          <p class="text-center text-sm">Period: {{ moh706.from }} to {{ moh706.to }}</p>
        </div>
        <div class="overflow-x-auto shadow-sm rounded border border-gray-200">
          <table class="w-full text-sm text-left text-gray-700">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2">#</th>
                <th class="px-3 py-2">Date</th>
                <th class="px-3 py-2">Patient Name</th>
                <th class="px-3 py-2">Age</th>
                <th class="px-3 py-2">Sex</th>
                <th class="px-3 py-2">OP No.</th>
                <th class="px-3 py-2">Test Requested</th>
                <th class="px-3 py-2">Sample</th>
                <th class="px-3 py-2">Result</th>
                <th class="px-3 py-2">Comments</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="row in moh706.data" :key="row.serial_no" class="hover:bg-gray-50">
                <td class="px-3 py-2">{{ row.serial_no }}</td>
                <td class="px-3 py-2 whitespace-nowrap">{{ row.date }}</td>
                <td class="px-3 py-2">{{ row.patient_name }}</td>
                <td class="px-3 py-2">{{ row.age }}</td>
                <td class="px-3 py-2">{{ row.sex }}</td>
                <td class="px-3 py-2 whitespace-nowrap">{{ row.op_no }}</td>
                <td class="px-3 py-2">{{ row.test_requested }}</td>
                <td class="px-3 py-2">{{ row.sample_type }}</td>
                <td class="px-3 py-2">{{ row.result }}</td>
                <td class="px-3 py-2">{{ row.comments }}</td>
              </tr>
              <tr v-if="!moh706.loading && !moh706.data.length">
                <td colspan="10" class="px-4 py-8 text-center text-gray-400">Select a date range and click Generate</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <PaginationComponent
        v-if="moh706.meta"
        :currentPage="moh706.page"
        :totalPages="moh706.meta.total_pages"
        :totalItems="moh706.meta.total_items"
        :itemsPerPage="moh706.perPage"
        @update:currentPage="(p) => { moh706.page = p; fetchMoh706() }"
        @update:changePageSize="(s) => { moh706.perPage = Number(s); moh706.page = 1; fetchMoh706() }"
      />
    </section>

    <!-- MOH 705A -->
    <section v-if="currentForm === 'moh705a'">
      <div class="flex items-center justify-between mb-3">
        <div>
          <h2 class="text-lg font-bold text-gray-800">MOH 705A — Outpatient Morbidity (Adults ≥5 yrs)</h2>
          <p class="text-sm text-gray-500">Diagnoses grouped by disease — patients aged 5 and above</p>
        </div>
        <button
          v-if="moh705a.data.length"
          @click="printSection('moh705a-content')"
          class="px-4 py-2 border border-custom-blue text-custom-blue rounded hover:bg-blue-50 text-sm font-medium"
        >Print MOH 705A</button>
      </div>
      <div class="flex items-center gap-4 mb-4">
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">From:</label>
          <input v-model="moh705a.from" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">To:</label>
          <input v-model="moh705a.to" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <button @click="fetchMoh705a" :disabled="moh705a.loading"
          class="px-5 py-2 bg-custom-yellow text-custom-blue font-semibold rounded hover:bg-yellow-400 text-sm">
          {{ moh705a.loading ? 'Loading...' : 'Generate' }}
        </button>
      </div>
      <div id="moh705a-content">
        <div class="print:block hidden mb-4">
          <div class="flex flex-col items-center mb-3">
            <img v-if="facility.logo_url" :src="facility.logo_url" alt="Logo" class="h-16 object-contain mb-1" />
            <p class="font-bold text-center">{{ facility.FacilityName }}</p>
            <p v-if="facilityLocation" class="text-xs text-center text-gray-600">{{ facilityLocation }}</p>
          </div>
          <h1 class="text-xl font-bold text-center">MOH 705A — Outpatient Morbidity Summary (Adults ≥5 Years)</h1>
          <p class="text-center text-sm">Period: {{ moh705a.from }} to {{ moh705a.to }}</p>
        </div>
        <div class="overflow-x-auto shadow-sm rounded border border-gray-200">
          <table class="w-full text-sm text-left text-gray-700">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2">#</th>
                <th class="px-3 py-2">Disease / Condition</th>
                <th class="px-3 py-2">ICD Code</th>
                <th class="px-3 py-2 text-center">Male</th>
                <th class="px-3 py-2 text-center">Female</th>
                <th class="px-3 py-2 text-center">Total</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(row, idx) in moh705a.data" :key="idx" class="hover:bg-gray-50">
                <td class="px-3 py-2">{{ (moh705a.page - 1) * moh705a.perPage + idx + 1 }}</td>
                <td class="px-3 py-2">{{ row.diagnosis }}</td>
                <td class="px-3 py-2 text-gray-500">{{ row.code || '—' }}</td>
                <td class="px-3 py-2 text-center">{{ row.male }}</td>
                <td class="px-3 py-2 text-center">{{ row.female }}</td>
                <td class="px-3 py-2 text-center font-semibold">{{ row.total }}</td>
              </tr>
              <tr v-if="!moh705a.loading && !moh705a.data.length">
                <td colspan="6" class="px-4 py-8 text-center text-gray-400">Select a date range and click Generate</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <PaginationComponent
        v-if="moh705a.meta"
        :currentPage="moh705a.page"
        :totalPages="moh705a.meta.total_pages"
        :totalItems="moh705a.meta.total_items"
        :itemsPerPage="moh705a.perPage"
        @update:currentPage="(p) => { moh705a.page = p; fetchMoh705a() }"
        @update:changePageSize="(s) => { moh705a.perPage = Number(s); moh705a.page = 1; fetchMoh705a() }"
      />
    </section>

    <!-- MOH 705B -->
    <section v-if="currentForm === 'moh705b'">
      <div class="flex items-center justify-between mb-3">
        <div>
          <h2 class="text-lg font-bold text-gray-800">MOH 705B — Outpatient Morbidity (Children &lt;5 yrs)</h2>
          <p class="text-sm text-gray-500">Diagnoses grouped by disease — patients under 5 years of age</p>
        </div>
        <button
          v-if="moh705b.data.length"
          @click="printSection('moh705b-content')"
          class="px-4 py-2 border border-custom-blue text-custom-blue rounded hover:bg-blue-50 text-sm font-medium"
        >Print MOH 705B</button>
      </div>
      <div class="flex items-center gap-4 mb-4">
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">From:</label>
          <input v-model="moh705b.from" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">To:</label>
          <input v-model="moh705b.to" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <button @click="fetchMoh705b" :disabled="moh705b.loading"
          class="px-5 py-2 bg-custom-yellow text-custom-blue font-semibold rounded hover:bg-yellow-400 text-sm">
          {{ moh705b.loading ? 'Loading...' : 'Generate' }}
        </button>
      </div>
      <div id="moh705b-content">
        <div class="print:block hidden mb-4">
          <div class="flex flex-col items-center mb-3">
            <img v-if="facility.logo_url" :src="facility.logo_url" alt="Logo" class="h-16 object-contain mb-1" />
            <p class="font-bold text-center">{{ facility.FacilityName }}</p>
            <p v-if="facilityLocation" class="text-xs text-center text-gray-600">{{ facilityLocation }}</p>
          </div>
          <h1 class="text-xl font-bold text-center">MOH 705B — Outpatient Morbidity Summary (Children &lt;5 Years)</h1>
          <p class="text-center text-sm">Period: {{ moh705b.from }} to {{ moh705b.to }}</p>
        </div>
        <div class="overflow-x-auto shadow-sm rounded border border-gray-200">
          <table class="w-full text-sm text-left text-gray-700">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2">#</th>
                <th class="px-3 py-2">Disease / Condition</th>
                <th class="px-3 py-2">ICD Code</th>
                <th class="px-3 py-2 text-center">Male</th>
                <th class="px-3 py-2 text-center">Female</th>
                <th class="px-3 py-2 text-center">Total</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(row, idx) in moh705b.data" :key="idx" class="hover:bg-gray-50">
                <td class="px-3 py-2">{{ (moh705b.page - 1) * moh705b.perPage + idx + 1 }}</td>
                <td class="px-3 py-2">{{ row.diagnosis }}</td>
                <td class="px-3 py-2 text-gray-500">{{ row.code || '—' }}</td>
                <td class="px-3 py-2 text-center">{{ row.male }}</td>
                <td class="px-3 py-2 text-center">{{ row.female }}</td>
                <td class="px-3 py-2 text-center font-semibold">{{ row.total }}</td>
              </tr>
              <tr v-if="!moh705b.loading && !moh705b.data.length">
                <td colspan="6" class="px-4 py-8 text-center text-gray-400">Select a date range and click Generate</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <PaginationComponent
        v-if="moh705b.meta"
        :currentPage="moh705b.page"
        :totalPages="moh705b.meta.total_pages"
        :totalItems="moh705b.meta.total_items"
        :itemsPerPage="moh705b.perPage"
        @update:currentPage="(p) => { moh705b.page = p; fetchMoh705b() }"
        @update:changePageSize="(s) => { moh705b.perPage = Number(s); moh705b.page = 1; fetchMoh705b() }"
      />
    </section>

    <!-- MOH 717 -->
    <section v-if="currentForm === 'moh717'">
      <div class="flex items-center justify-between mb-3">
        <div>
          <h2 class="text-lg font-bold text-gray-800">MOH 717 — Monthly Service Workload Summary</h2>
          <p class="text-sm text-gray-500">OPD attendance, lab tests, and pharmacy for the selected period</p>
        </div>
        <button
          v-if="moh717.data"
          @click="printSection('moh717-content')"
          class="px-4 py-2 border border-custom-blue text-custom-blue rounded hover:bg-blue-50 text-sm font-medium"
        >Print MOH 717</button>
      </div>

      <div class="flex items-center gap-4 mb-6">
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">From:</label>
          <input v-model="moh717.from" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <div class="flex items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">To:</label>
          <input v-model="moh717.to" type="date" class="px-3 py-2 border rounded-md" />
        </div>
        <button @click="fetchMoh717" :disabled="moh717.loading"
          class="px-5 py-2 bg-custom-yellow text-custom-blue font-semibold rounded hover:bg-yellow-400 text-sm">
          {{ moh717.loading ? 'Loading...' : 'Generate' }}
        </button>
      </div>

      <div v-if="!moh717.data && !moh717.loading" class="text-center py-12 text-gray-400 text-sm">
        Select a date range and click Generate
      </div>

      <div v-if="moh717.data" id="moh717-content" class="space-y-6">
        <div class="hidden print:block mb-4">
          <div class="flex flex-col items-center mb-3">
            <img v-if="facility.logo_url" :src="facility.logo_url" alt="Logo" class="h-16 object-contain mb-1" />
            <p class="font-bold text-center">{{ facility.FacilityName }}</p>
            <p v-if="facilityLocation" class="text-xs text-center text-gray-600">{{ facilityLocation }}</p>
          </div>
          <h1 class="text-xl font-bold text-center">MOH 717 — Monthly Service Workload Summary</h1>
          <p class="text-center text-sm">Period: {{ moh717.from }} to {{ moh717.to }}</p>
        </div>

        <!-- OPD Attendance -->
        <div>
          <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">A. Outpatient Attendance</h3>
          <div class="overflow-x-auto rounded border border-gray-200">
            <table class="w-full text-sm text-gray-700">
              <thead class="bg-gray-100">
                <tr>
                  <th class="px-3 py-2 text-left" rowspan="2">Category</th>
                  <th class="px-3 py-2 text-center border-l" colspan="5">Male</th>
                  <th class="px-3 py-2 text-center border-l" colspan="5">Female</th>
                  <th class="px-3 py-2 text-center border-l" rowspan="2">Grand Total</th>
                </tr>
                <tr>
                  <th class="px-3 py-1 text-center border-l text-xs">&lt;5</th>
                  <th class="px-3 py-1 text-center text-xs">5–14</th>
                  <th class="px-3 py-1 text-center text-xs">15–59</th>
                  <th class="px-3 py-1 text-center text-xs">60+</th>
                  <th class="px-3 py-1 text-center text-xs font-semibold">Total</th>
                  <th class="px-3 py-1 text-center border-l text-xs">&lt;5</th>
                  <th class="px-3 py-1 text-center text-xs">5–14</th>
                  <th class="px-3 py-1 text-center text-xs">15–59</th>
                  <th class="px-3 py-1 text-center text-xs">60+</th>
                  <th class="px-3 py-1 text-center text-xs font-semibold">Total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="row in [
                  { label: 'New Patients', key: 'new' },
                  { label: 'Re-visits', key: 'revisit' }
                ]" :key="row.key" class="hover:bg-gray-50">
                  <td class="px-3 py-2 font-medium">{{ row.label }}</td>
                  <td class="px-3 py-2 text-center border-l">{{ moh717.data.opd[row.key].male.under_5 }}</td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd[row.key].male.a5_to_14 }}</td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd[row.key].male.a15_to_59 }}</td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd[row.key].male.a60_plus }}</td>
                  <td class="px-3 py-2 text-center font-semibold">{{ moh717.data.opd[row.key].male.total }}</td>
                  <td class="px-3 py-2 text-center border-l">{{ moh717.data.opd[row.key].female.under_5 }}</td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd[row.key].female.a5_to_14 }}</td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd[row.key].female.a15_to_59 }}</td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd[row.key].female.a60_plus }}</td>
                  <td class="px-3 py-2 text-center font-semibold">{{ moh717.data.opd[row.key].female.total }}</td>
                  <td class="px-3 py-2 text-center font-bold">{{ moh717.data.opd[row.key].total }}</td>
                </tr>
                <tr class="bg-gray-50 font-bold">
                  <td class="px-3 py-2">Total OPD</td>
                  <td class="px-3 py-2 text-center border-l" colspan="5"></td>
                  <td class="px-3 py-2 text-center border-l" colspan="5"></td>
                  <td class="px-3 py-2 text-center">{{ moh717.data.opd.total }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- Department summary cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <div class="rounded border border-gray-200 p-4">
            <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">B. Triage</h3>
            <div class="flex justify-between items-center py-2">
              <span class="text-sm text-gray-600">Patients triaged</span>
              <span class="font-bold text-gray-900">{{ moh717.data.triage.visits_triaged }}</span>
            </div>
          </div>
  
          <div class="rounded border border-gray-200 p-4">
            <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">C. Consultation</h3>
            <div class="flex justify-between items-center py-2">
              <span class="text-sm text-gray-600">Patients consulted</span>
              <span class="font-bold text-gray-900">{{ moh717.data.consultation.visits_consulted }}</span>
            </div>
          </div>
  
          <div class="rounded border border-gray-200 p-4">
            <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">D. Laboratory</h3>
            <div class="flex justify-between items-center py-2">
              <span class="text-sm text-gray-600">Tests completed & published</span>
              <span class="font-bold text-gray-900">{{ moh717.data.laboratory.total_tests_done }}</span>
            </div>
          </div>
  
          <div class="rounded border border-gray-200 p-4">
            <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">E. Radiology</h3>
            <div class="flex justify-between items-center py-2">
              <span class="text-sm text-gray-600">Tests completed</span>
              <span class="font-bold text-gray-900">{{ moh717.data.radiology.total_tests_done }}</span>
            </div>
          </div>
  
          <div class="rounded border border-gray-200 p-4">
            <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">F. Pharmacy</h3>
            <div class="flex justify-between items-center py-2 border-b border-gray-100">
              <span class="text-sm text-gray-600">Dispensing encounters</span>
              <span class="font-bold text-gray-900">{{ moh717.data.pharmacy.encounters }}</span>
            </div>
            <div class="flex justify-between items-center py-2">
              <span class="text-sm text-gray-600">Items actually dispensed</span>
              <span class="font-bold text-gray-900">{{ moh717.data.pharmacy.items_dispensed }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toast-notification'
import apiClient from '@/services/api'
import PaginationComponent from '@/components/shared/pagination.vue'

const toast = useToast()

const facility = ref({})
const facilityLocation = ref('')

onMounted(async () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}')
    if (!stored?.FacilityId) return
    const res = await apiClient.fetchFacility(stored.FacilityId)
    if (res.success) {
      facility.value = res.data
      facilityLocation.value = [res.data?.NearestTown, res.data?.County].filter(Boolean).join(', ')
    }
  } catch {
    // Facility signature is a header nicety — silently skip if it can't be fetched.
  }
})

const currentForm = ref('moh706')
const tabs = [
  { key: 'moh706',  label: 'MOH 706 — Lab Register' },
  { key: 'moh705a', label: 'MOH 705A — Adults (≥5 yrs)' },
  { key: 'moh705b', label: 'MOH 705B — Children (<5 yrs)' },
  { key: 'moh717', label: 'MOH 717 — Workload Summary' },
]

const moh706  = ref({ from: '', to: '', loading: false, data: [], meta: null, page: 1, perPage: 10 })
const moh705a = ref({ from: '', to: '', loading: false, data: [], meta: null, page: 1, perPage: 10 })
const moh705b = ref({ from: '', to: '', loading: false, data: [], meta: null, page: 1, perPage: 10 })
const moh717 = ref({ from: '', to: '', loading: false, data: null, meta: null, page: 1, perPage: 10 })

const fetchMoh706 = async () => {
  moh706.value.loading = true
  try {
    const res = await apiClient.getMoh706Report({
      from_date: moh706.value.from, to_date: moh706.value.to,
      page: moh706.value.page, per_page: moh706.value.perPage
    })
    if (res.success) { moh706.value.data = res.data; moh706.value.meta = res.meta }
    else toast.error(res.message || 'Failed to load MOH 706')
  } catch { toast.error('Network error') } finally { moh706.value.loading = false }
}

const fetchMoh705a = async () => {
  moh705a.value.loading = true
  try {
    const res = await apiClient.getMoh705aReport({
      from_date: moh705a.value.from, to_date: moh705a.value.to,
      page: moh705a.value.page, per_page: moh705a.value.perPage
    })
    if (res.success) { moh705a.value.data = res.data; moh705a.value.meta = res.meta }
    else toast.error(res.message || 'Failed to load MOH 705A')
  } catch { toast.error('Network error') } finally { moh705a.value.loading = false }
}

const fetchMoh705b = async () => {
  moh705b.value.loading = true
  try {
    const res = await apiClient.getMoh705bReport({
      from_date: moh705b.value.from, to_date: moh705b.value.to,
      page: moh705b.value.page, per_page: moh705b.value.perPage
    })
    if (res.success) { moh705b.value.data = res.data; moh705b.value.meta = res.meta }
    else toast.error(res.message || 'Failed to load MOH 705B')
  } catch { toast.error('Network error') } finally { moh705b.value.loading = false }
}

const fetchMoh717 = async () => {
  moh717.value.loading = true
  try {
    const res = await apiClient.getMoh717Report({
      from_date: moh717.value.from,
      to_date: moh717.value.to
    })
    if (res.success) {
      moh717.value.data = res.data
      toast.success("MOH 717 Report fetched")
    } else {
      toast.error(res.message || "MOH 717 Report failed to fetch")
    }

  } catch {
    toast.error("Network Error")
  } finally {
    moh717.value.loading = false
  }
}

const printSection = (sectionId) => {
  const style = document.createElement('style')
  style.id = 'moh-print-style'
  style.innerHTML = `
    @page { size: A4 landscape; margin: 10mm; }
    @media print {
      body * { visibility: hidden; }
      #${sectionId}, #${sectionId} * { visibility: visible; }
      #${sectionId} { position: absolute; top: 0; left: 0; width: 100%; }
      .print\\:block { display: block !important; }
    }
  `
  document.head.appendChild(style)
  window.print()
  setTimeout(() => document.getElementById('moh-print-style')?.remove(), 1000)
}
</script>
